<template>
  <div class="min-h-screen bg-surface-50 dark:bg-dark-900 transition-colors">
    <!-- Mobile menu button -->
    <div class="lg:hidden fixed top-4 left-4 z-50">
      <button @click="isSidebarOpen = !isSidebarOpen"
        class="p-2 rounded-lg bg-white dark:bg-dark-800 shadow-md text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-colors">
        <i :class="isSidebarOpen ? 'fas fa-times' : 'fas fa-bars'" class="text-xl"></i>
      </button>
    </div>

    <!-- Notification Bell -->
    <div class="fixed top-4 right-4 z-50">
      <NotificationBell />
    </div>

    <!-- Sidebar with Navigation -->
    <aside
      class="fixed inset-y-0 left-0 transform lg:transform-none lg:translate-x-0 transition-all duration-200 ease-in-out z-40 bg-white dark:bg-dark-800 w-64 shadow-xl lg:shadow-none"
      :class="isSidebarOpen ? 'translate-x-0' : '-translate-x-full'">
      <Navigation />
    </aside>

    <!-- Overlay -->
    <div v-if="isSidebarOpen" class="fixed inset-0 bg-black/50 lg:hidden z-30 transition-opacity"
      @click="closeOverlays"></div>

    <!-- Main Content -->
    <div class="lg:pl-64">
      <main class="min-h-screen py-6 bg-surface-50 dark:bg-dark-900 transition-colors">
        <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1920px] pt-14 lg:pt-0">
          <router-view></router-view>
        </div>
      </main>
    </div>

    <!-- Global Floating Action Buttons (for users with sales dashboard access) -->
    <!-- Hide on SalesDashboard page since it has its own FAB -->
    <FloatingActionButtons 
      v-if="canAccessSalesDashboard && route.name !== 'sales-dashboard'"
      :hasPermission="hasPermission"
      @create-order="handleCreateOrder"
      @create-client="handleCreateClient"
      @add-payment="handleAddPayment"
      @create-expense="handleCreateExpense"
      @create-quotation="handleCreateQuotation"
      @generate-report="handleGenerateReport"
      @tools="handleTools"
    />

    <!-- Global Modals for FAB Actions -->
    <GlobalFabModals />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Navigation from '../components/Navigation.vue';
import NotificationBell from '../components/NotificationBell.vue';
import FloatingActionButtons from '../components/dashboard/FloatingActionButtons.vue';
import GlobalFabModals from '../components/dashboard/GlobalFabModals.vue';
import { useAuthStore } from '../stores/auth';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const isSidebarOpen = ref(false);

// Check if user can access sales dashboard (has salesdashboard.view permission)
const canAccessSalesDashboard = computed(() => {
  return authStore.hasPermission('salesdashboard.view');
});

// Permission check function for FloatingActionButtons
const hasPermission = (permission) => {
  return authStore.hasPermission(permission);
};

// Handle FAB actions - dispatch global events
const handleCreateOrder = () => {
  if (hasPermission('orders.create')) {
    window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'create-order' } }));
  }
};

const handleCreateClient = () => {
  window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'create-client' } }));
};

const handleAddPayment = () => {
  window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'add-payment' } }));
};

const handleCreateExpense = () => {
  if (hasPermission('expenses.create')) {
    window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'create-expense' } }));
  }
};

const handleCreateQuotation = () => {
  window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'create-quotation' } }));
};

const handleGenerateReport = () => {
  // Navigate to sales dashboard for report generation
  router.push({ name: 'sales-dashboard' });
};

const handleTools = () => {
  // Dispatch event to open tools modal globally
  window.dispatchEvent(new CustomEvent('fab-action', { detail: { action: 'tools' } }));
};

const closeOverlays = () => {
  isSidebarOpen.value = false;
};

// Listen for custom event from Navigation component to close sidebar
const handleCloseSidebar = () => {
  isSidebarOpen.value = false;
};

onMounted(() => {
  window.addEventListener('close-sidebar', handleCloseSidebar);
});

onUnmounted(() => {
  window.removeEventListener('close-sidebar', handleCloseSidebar);
});
</script>

<style scoped>
/* Remove conflicting router-link-active style */
</style>